import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import {
  Shield,
  Zap,
  AlertCircle,
  Sparkles,
  Users,
  BarChart3,
  Smartphone
} from 'lucide-react';
import { getApiBaseUrl } from '../config/apiBaseUrl';
import {
  UnweeProviderBadges,
  type LoginProviderBadge,
} from '../components/UnweeProviderBadges';
import {
  authorizeUrlWithoutForcedLogin,
  isSilentSsoInteractionError,
  markSilentSsoFailedThisVisit,
} from '../utils/silentSso';

const LoginPage: React.FC = () => {
  const { showError } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [unweeProviders, setUnweeProviders] = useState<LoginProviderBadge[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('silent_sso') === 'failed') {
      markSilentSsoFailedThisVisit();
    }
    const oe = params.get('oauth_error');
    if (oe && !isSilentSsoInteractionError(oe)) {
      showError('Connexion', oe);
    }
    if (oe || params.get('silent_sso')) {
      window.history.replaceState({}, '', '/login');
    }
  }, [showError]);

  const startAssoweeLogin = async () => {
    setError(null);
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/sso/start`, {
        credentials: 'include',
      });
      if (!response.ok) {
        setError('Connexion UnWee indisponible');
        return;
      }
      const data = await response.json();
      if (!data?.authUrl) {
        setError('Connexion UnWee indisponible');
        return;
      }
      window.location.assign(authorizeUrlWithoutForcedLogin(data.authUrl));
    } catch {
      setError('Connexion UnWee indisponible');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/auth/oauth-config`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        if (Array.isArray(data.providers)) {
          setUnweeProviders(
            data.providers.filter(
              (provider: LoginProviderBadge) =>
                provider &&
                typeof provider.id === 'string' &&
                typeof provider.label === 'string',
            ),
          );
        }
      } catch {
        // Ne pas masquer le bouton si la sonde échoue (CORS réseau, etc.)
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo & Branding */}
          <div className="flex items-center space-x-4 mb-16">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl overflow-hidden p-1">
              <img src="/logo-carton.svg" alt="" className="w-11 h-11 object-contain drop-shadow-md" width={44} height={44} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white mb-1">InventWee</h1>
              <p className="text-indigo-100 text-lg font-medium">Système d'Inventaire</p>
              <p className="text-indigo-200 text-sm">Développé par AirZk.fr</p>
            </div>
          </div>

          {/* Features avec animations */}
          <div className="space-y-10">
            <div className="flex items-start space-x-5 group">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-2">Scan Ultra-Rapide</h3>
                <p className="text-indigo-100 text-base leading-relaxed">
                  Scannez vos objets pédagogiques en quelques secondes avec notre technologie QR avancée et reconnaissance d'objets IA
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-5 group">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-2">Synchronisation Temps Réel</h3>
                <p className="text-indigo-100 text-base leading-relaxed">
                  Vos données sont synchronisées instantanément sur tous vos appareils avec une architecture offline-first
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-5 group">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-2">Analytics Avancés</h3>
                <p className="text-indigo-100 text-base leading-relaxed">
                  Suivez l'utilisation, les mouvements et générez des rapports détaillés de votre inventaire
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <div className="flex items-center space-x-6 text-indigo-200 text-sm">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Multi-utilisateurs</span>
            </div>
            <div className="flex items-center space-x-2">
              <Smartphone className="w-4 h-4" />
              <span>Mobile-First</span>
            </div>
          </div>
          <p className="text-indigo-200 text-sm mt-4">
            © 2025 InventWee. Tous droits réservés.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl shadow-2xl mb-6 p-2">
              <img src="/logo-carton.svg" alt="" className="w-14 h-14 object-contain drop-shadow" width={56} height={56} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">InventWee</h1>
            <p className="text-gray-600 font-medium">Système d'Inventaire</p>
            <p className="text-gray-500 text-sm">Développé par AirZk.fr</p>
          </div>

          {/* Welcome Section */}
          <div className="text-center lg:text-left mb-10">
            <div className="flex items-center justify-center lg:justify-start space-x-2 mb-4">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <h2 className="text-4xl font-bold text-gray-900">
                Bon retour !
              </h2>
            </div>
            <p className="text-gray-600 text-lg">
              Connectez-vous pour accéder à votre inventaire
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
            <button
              type="button"
              onClick={() => void startAssoweeLogin()}
              className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-indigo-200 bg-indigo-600 text-white font-medium shadow-sm hover:bg-indigo-700 transition-colors"
            >
              Se connecter avec UnWee
            </button>
            <UnweeProviderBadges providers={unweeProviders} />
            {error && (
              <div className="mt-4 flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-700 text-sm font-medium">{error}</span>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Besoin d'aide ?{' '}
              <a href="#" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors duration-200">
                Contactez le support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;